import { Check, ChevronDown } from "lucide-react";
import { useNavigate, useParams } from "react-router";

import { useGetBuiltinUsersQuery, useGetSubscriptionsQuery } from "@/api/api";
import { useSpace, useSpaceIdentity } from "@/api/hooks";
import { HGUser } from "@/common/types";
import { userPath } from "@/common/urls";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { BrandDiamond } from "./BrandMark";
import FollowButton from "./FollowButton";
import UserAvatar from "./UserAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { SidebarMenuButton } from "../sidebar";

/**
 * The follow control for the space being browsed.
 *
 * Sits under the switcher rather than on it: the header band is the identity,
 * and crowding an action into it leaves neither enough room to read cleanly.
 * Renders nothing in your own space — there is no one to follow there.
 */
export function SpaceFollowBanner() {
  const me = useAppSelector(selectMe);
  const { isOwn, owner } = useSpace();

  if (!me || isOwn || !owner) return null;

  return (
    <div className="flex shrink-0 justify-end px-2 pt-2">
      <FollowButton user={owner} />
    </div>
  );
}

/**
 * Which person's collections the sidebar is showing, and the way to change it.
 *
 * Subscriptions used to be a tab sitting beside Collections, but the two are
 * not peers: choosing a person *scopes* the collection list. As a switcher the
 * list below simply reloads in place, so picking someone no longer strands you
 * on the wrong tab.
 */
export default function SpaceSwitcher() {
  const navigate = useNavigate();
  const { userID } = useParams();
  const me = useAppSelector(selectMe);
  const { isOwn, owner } = useSpace();
  const { data: subscriptions } = useGetSubscriptionsQuery(undefined, {
    skip: !me,
  });
  // Signed out there are no subscriptions to offer, so the admin-curated list
  // stands in for them. Skipped when signed in: that page never shows it.
  const { data: builtins } = useGetBuiltinUsersQuery(undefined, { skip: !!me });

  // Signed out on "/" nobody is selected at all, and what the switcher is
  // sitting on is the site itself.
  const isSite = !me && !userID;
  const label = isSite
    ? "Eventera"
    : isOwn
      ? me?.name || "Your collections"
      : owner?.name || "Collections";
  const { name: avatarName, picture } = useSpaceIdentity();

  const row = (
    <>
      {isSite ? (
        <BrandDiamond />
      ) : (
        <UserAvatar name={avatarName} picture={picture} className="size-6" />
      )}
      {/* min-w-0 so a long name ellipsises instead of shoving the chevron out */}
      <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">
        {label}
      </span>
    </>
  );

  const goTo = (path: string) => () => navigate(path);

  const trigger = (
    <DropdownMenuTrigger asChild>
      <SidebarMenuButton
        aria-label="Switch collections owner"
        className="h-9 px-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
      >
        {row}
        <ChevronDown className="ml-auto size-4 shrink-0 opacity-60" />
      </SidebarMenuButton>
    </DropdownMenuTrigger>
  );

  // Which entry is ticked is a fact about the URL, not about a lookup that may
  // still be out: moving between two people leaves `owner` on the previous one
  // until the next resolves, and the check would sit on the wrong row. Both
  // spellings are accepted because /{id} is redirected to /{nickname}.
  const isSelected = (user: HGUser) =>
    !!userID && (userID === user.id || userID === user.nickname);

  // One entry shape for both lists: an entry must not look different depending
  // on whether it came from your subscriptions or from the built-in list.
  const userItem = (user: HGUser) => (
    <DropdownMenuItem key={user.id} onClick={goTo(userPath(user))}>
      <UserAvatar
        name={user.name || user.nickname}
        picture={user.picture}
        className="size-5"
      />
      <span className="min-w-0 flex-1 truncate">
        {user.name || `User #${user.id}`}
      </span>
      {isSelected(user) && <Check className="size-4 shrink-0" />}
    </DropdownMenuItem>
  );

  if (!me) {
    const featured: HGUser[] = builtins ?? [];

    // With nothing to switch to, show whose page this is and leave it at that:
    // a menu holding only the entry you are already on is not a choice. This
    // is also what the list still loading looks like.
    if (featured.length === 0) {
      if (!owner) return null;
      return (
        <div className="flex min-w-0 flex-1 items-center gap-2 px-1 text-foreground">
          {row}
        </div>
      );
    }

    return (
      <DropdownMenu>
        {trigger}
        <DropdownMenuContent
          align="start"
          side="bottom"
          sideOffset={4}
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
        >
          {/* The way back out of a person's space, where a signed-in visitor
              has "your collections" instead. */}
          <DropdownMenuItem onClick={goTo("/")}>
            <BrandDiamond />
            <span className="min-w-0 flex-1 truncate">Eventera</span>
            {isSite && <Check className="size-4 shrink-0" />}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuLabel className="text-[10.5px] font-bold uppercase tracking-[.1em] text-muted-foreground">
            Featured
          </DropdownMenuLabel>
          {featured.map(userItem)}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const followed: HGUser[] = subscriptions ?? [];

  return (
    <DropdownMenu>
      {trigger}
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
      >
        <DropdownMenuItem onClick={goTo("/")}>
          <UserAvatar
            name={me?.name || me?.nickname?.nickname || me?.id}
            picture={me?.picture}
            className="size-5"
          />
          <span className="min-w-0 flex-1 truncate">
            {me?.name || "Your collections"}
          </span>
          {isOwn && <Check className="size-4 shrink-0" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {followed.length === 0 ? (
          <div className="px-2 py-1.5 text-[12.5px] text-muted-foreground">
            You&rsquo;re not following anyone yet.
          </div>
        ) : (
          <>
            <DropdownMenuLabel className="text-[10.5px] font-bold uppercase tracking-[.1em] text-muted-foreground">
              Following
            </DropdownMenuLabel>
            {followed.map(userItem)}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
